import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Checkbox, Collapse, DatePicker, Descriptions, Divider, Form, Input, List as AntList, Modal, Popconfirm, Popover, Radio, Rate, Segmented, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography, Upload } from "antd";
import type { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile } from "antd/es/upload/interface";
import { List, useTable } from "@refinedev/antd";
import { getDefaultFilter, useList } from "@refinedev/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { api, restBaseUrl } from "@/providers/rest";

// Настройка dayjs для корректной работы с UTC временем
dayjs.extend(utc);

// Хелпер для форматирования даты с конвертацией UTC -> локальное время
const formatLocalDate = (date: string | null | undefined, format: string = "DD.MM.YYYY HH:mm") => {
  if (!date) return "";
  return dayjs.utc(date).local().format(format);
};
import { useNavigate } from "react-router-dom";
import { AppstoreOutlined, CheckOutlined, ClockCircleOutlined, CloseOutlined, DeleteOutlined, EyeOutlined, FilterOutlined, InboxOutlined, PlayCircleOutlined, PlusOutlined, SearchOutlined, StarFilled, SyncOutlined, TableOutlined, UploadOutlined } from "@ant-design/icons";
import { useNotify } from "@/hooks/useNotify";
import { RESPONSE_STATUSES } from "@/utils/formatters";
import { resolveApiBase } from "@/providers/api-base";

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

export default function ReviewsList() {
  const navigate = useNavigate();
  const notify = useNotify();

  // Восстанавливаем состояние табов из sessionStorage
  const [responseTab, setResponseTabState] = useState<'all' | 'with_response' | 'without_response'>(() => {
    const saved = sessionStorage.getItem('reviews_responseTab');
    return (saved as 'all' | 'with_response' | 'without_response') || 'all';
  });
  const [responseStatusFilter, setResponseStatusFilter] = useState<string | undefined>(undefined);
  const [typeTab, setTypeTabState] = useState<'all' | 'review' | 'question'>(() => {
    const saved = sessionStorage.getItem('reviews_typeTab');
    return (saved as 'all' | 'review' | 'question') || 'all';
  });

  // Обёртки для сохранения в sessionStorage при изменении
  const setResponseTab = (value: 'all' | 'with_response' | 'without_response') => {
    sessionStorage.setItem('reviews_responseTab', value);
    setResponseTabState(value);
  };
  const setTypeTab = (value: 'all' | 'review' | 'question') => {
    sessionStorage.setItem('reviews_typeTab', value);
    setTypeTabState(value);
  };

  // Вычисляем начальные фильтры на основе sessionStorage (один раз при монтировании)
  const [initialFiltersComputed] = useState(() => {
    const savedResponseTab = sessionStorage.getItem('reviews_responseTab') as 'all' | 'with_response' | 'without_response' || 'all';
    const savedTypeTab = sessionStorage.getItem('reviews_typeTab') as 'all' | 'review' | 'question' || 'all';

    const filters: any[] = [
      { field: "status", operator: "eq", value: undefined },
      { field: "source", operator: "eq", value: undefined },
      { field: "q", operator: "contains", value: undefined },
      { field: "date_from", operator: "eq", value: undefined },
      { field: "date_to", operator: "eq", value: undefined },
    ];

    // Добавляем фильтр response_status на основе сохранённого таба
    if (savedResponseTab === 'with_response') {
      filters.push({ field: "response_status", operator: "nnull", value: true });
    } else if (savedResponseTab === 'without_response') {
      filters.push({ field: "response_status", operator: "null", value: true });
    } else {
      filters.push({ field: "response_status", operator: "eq", value: undefined });
    }

    // Добавляем фильтр type на основе сохранённого таба
    if (savedTypeTab !== 'all') {
      filters.push({ field: "type", operator: "eq", value: savedTypeTab });
    } else {
      filters.push({ field: "type", operator: "eq", value: undefined });
    }

    return filters;
  });
  const [viewMode, setViewMode] = useState<'table' | 'stream'>('table');
  const [groupByProduct, setGroupByProduct] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [showGenerateSection, setShowGenerateSection] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(undefined);
  const [selectedStyle, setSelectedStyle] = useState<string | undefined>(undefined);
  const [generatedText, setGeneratedText] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalReview, setModalReview] = useState<any>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [autoSyncLoading, setAutoSyncLoading] = useState(false);
  const [syncMode, setSyncMode] = useState<"quick" | "medium" | "full">("medium");
  const [syncMarketplaces, setSyncMarketplaces] = useState<string[]>(["wildberries_cab1"]);
  const [syncContentType, setSyncContentType] = useState<"reviews" | "questions" | "both">("reviews");
  const [syncDateRange, setSyncDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(40); // percentage
  const [isResizing, setIsResizing] = useState(false);
  const [originalGeneratedText, setOriginalGeneratedText] = useState<string>("");
  const [createForm] = Form.useForm();
  const [skuSearch, setSkuSearch] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [filterType, setFilterType] = useState<'all' | 'with_photo'>('all');
  const [filterRatings, setFilterRatings] = useState<number[]>([]);
  const [filterBrands, setFilterBrands] = useState<string[]>([]);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [responseStatuses, setResponseStatuses] = useState<{value: string, label: string, count: number}[]>([]);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFields, setExportFields] = useState<string[]>([
    'date', 'product', 'sku', 'brand', 'rating', 'text', 'advantages', 'disadvantages',
    'author', 'generated_response', 'final_response', 'status', 'cabinet'
  ]);
  const [bulkGenerateModalOpen, setBulkGenerateModalOpen] = useState(false);
  const [bulkGenerateMode, setBulkGenerateMode] = useState<'ai' | 'template'>('ai');
  const [bulkGenerateTemplateId, setBulkGenerateTemplateId] = useState<string | undefined>();
  const [bulkGenerateStyleId, setBulkGenerateStyleId] = useState<string | undefined>();
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const handleMouseDown = () => {
    setIsResizing(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;

    const container = document.querySelector('.stream-container') as HTMLElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    // Limit between 20% and 80%
    if (newWidth >= 20 && newWidth <= 80) {
      setLeftPanelWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const {
    tableProps,
    filters,
    setFilters,
    sorters,
    setSorters,
    currentPage: hookCurrentPage,
    setCurrentPage,
    tableQuery,
  } = useTable<any, any, any>({
    resource: "reviews",
    pagination: { mode: "server", pageSize: 20 },
    sorters: {
      mode: "server",
      initial: [{ field: "marketplace_created_at", order: "desc" }],
    },
    filters: {
      initial: initialFiltersComputed,
    },
    syncWithLocation: true,
  });

  const appliedSearchValue = useMemo(() => {
    const f = getDefaultFilter("q", filters);
    return typeof f?.value === "string" ? f.value : "";
  }, [filters]);

  const [searchDraft, setSearchDraft] = useState("");
  useEffect(() => setSearchDraft(appliedSearchValue), [appliedSearchValue]);

  // Загрузка списка брендов
  useEffect(() => {
    api.get<{brands: string[]}>("/reviews/brands").then((res) => {
      setAvailableBrands(res.brands || []);
    }).catch(() => {});
  }, []);

  // Загрузка состояния авто-синхронизации
  useEffect(() => {
    fetch(`${restBaseUrl}/api/v1/settings/auto-sync`)
      .then((r) => r.json())
      .then((data) => setAutoSyncEnabled(data.enabled ?? false))
      .catch(() => {});
  }, []);

  // Загрузка статусов ответов (динамически, с учётом текущих фильтров)
  const loadResponseStatuses = (currentFilters: any[]) => {
    const params = new URLSearchParams();
    for (const f of currentFilters) {
      // Пропускаем только конкретный статус из дропдауна (operator=eq),
      // но передаём табовые фильтры null/nnull (Ждут ответа / Есть ответ)
      if (f.field === 'response_status' && f.operator === 'eq') continue;
      if (f.field === 'response_status' && (f.operator === 'null' || f.operator === 'nnull')) {
        params.set('response_status', f.operator === 'null' ? 'null' : 'nnull');
        continue;
      }
      if (f.value === undefined || f.value === null || f.value === '') continue;
      if (f.field === 'q' && f.operator === 'contains') {
        params.set('q', f.value);
      } else if (f.field === 'status') {
        params.set('status', f.value);
      } else if (f.field === 'source') {
        params.set('source', f.value);
      } else if (f.field === 'product_sku') {
        params.set('product_sku', f.value);
      } else if (f.field === 'has_stop_words') {
        params.set('has_stop_words', String(f.value));
      } else if (f.field === 'rating') {
        params.set('rating', f.value);
      } else if (f.field === 'brand') {
        params.set('brand', f.value);
      } else if (f.field === 'date_from') {
        params.set('date_from', f.value);
      } else if (f.field === 'date_to') {
        params.set('date_to', f.value);
      } else if (f.field === 'type' && f.value !== 'all') {
        params.set('type', f.value);
      }
    }
    const qs = params.toString();
    api.get<{statuses: {value: string, label: string, count: number}[]}>(`/reviews/response-statuses${qs ? '?' + qs : ''}`).then((res) => {
      setResponseStatuses(res.statuses || []);
    }).catch(() => {});
  };
  useEffect(() => {
    loadResponseStatuses(filters);
  }, [filters]);

  const updateSearchFilter = (value: string) => {
    const trimmed = value.trim();
    setFilters(
      [
        { field: "q", operator: "contains", value: trimmed.length ? trimmed : undefined },
      ],
      "merge",
    );
    if ((hookCurrentPage ?? 1) !== 1) setCurrentPage(1);
  };

  const handleTabChange = (key: string) => {
    setResponseTab(key as typeof responseTab);
    // Сбрасываем Select "Статус ответа" при смене таба
    setResponseStatusFilter(undefined);
    // Удаляем все существующие фильтры response_status и добавляем новый
    const otherFilters = filters.filter((f: any) => f.field !== 'response_status');
    if (key === 'all') {
      // Для "Все" - убираем фильтр response_status полностью
      setFilters(otherFilters, "replace");
    } else if (key === 'with_response') {
      // Оператор nnull - записи где response_status IS NOT NULL (есть ответ)
      setFilters([...otherFilters, { field: "response_status", operator: "nnull", value: true }], "replace");
    } else if (key === 'without_response') {
      // Оператор null - записи где response_status IS NULL (ждут ответа)
      setFilters([...otherFilters, { field: "response_status", operator: "null", value: true }], "replace");
    }
    if ((hookCurrentPage ?? 1) !== 1) setCurrentPage(1);
  };

  const handleTypeTabChange = (key: string) => {
    setTypeTab(key as typeof typeTab);
    if (key === 'all') {
      setFilters([{ field: "type", operator: "eq", value: undefined }], "merge");
    } else {
      setFilters([{ field: "type", operator: "eq", value: key }], "merge");
    }
    if ((hookCurrentPage ?? 1) !== 1) setCurrentPage(1);
  };

  // Получаем текущие фильтры БЕЗ response_status
  const baseFilters = useMemo(() => {
    return filters.filter(f => (f as any).field !== 'response_status');
  }, [filters]);

  // Fetch counts for tabs using separate queries with current filters
  const withResponseQuery = useList({
    resource: "reviews",
    filters: [...baseFilters, { field: "response_status", operator: "nnull", value: true }],
    queryOptions: { enabled: true },
    pagination: { mode: "off" },
  });

  const withoutResponseQuery = useList({
    resource: "reviews",
    filters: [...baseFilters, { field: "response_status", operator: "null", value: true }],
    queryOptions: { enabled: true },
    pagination: { mode: "off" },
  });

  const allCountQuery = useList({
    resource: "reviews",
    filters: baseFilters,
    queryOptions: { enabled: true },
    pagination: { mode: "off" },
  });

  // Calculate counts for tabs
  const allCount = allCountQuery.result?.total ?? 0;
  const dataItems = (tableQuery.data?.data as any[]) ?? [];
  const withResponseCount = withResponseQuery.result?.total ?? 0;
  const withoutResponseCount = withoutResponseQuery.result?.total ?? 0;

  // Группировка данных по товару
  const groupedData = useMemo(() => {
    if (!groupByProduct) return null;

    const groups: Record<string, { product: any; reviews: any[] }> = {};

    dataItems.forEach((item: any) => {
      const key = item.product_sku || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          product: {
            id: `group-${key}`,
            product_sku: item.product_sku,
            seller_sku: item.seller_sku,
            product_name: item.product_name,
            brand: item.brand,
            category: item.category,
            product_photo_url: item.product_photo_url,
          },
          reviews: []
        };
      }
      groups[key].reviews.push(item);
    });

    return Object.values(groups).map(g => ({
      ...g.product,
      children: g.reviews,
      reviewCount: g.reviews.length
    }));
  }, [dataItems, groupByProduct]);

  // Auto-select first review in stream mode
  useEffect(() => {
    if (viewMode === 'stream' && dataItems.length > 0 && !selectedReviewId) {
      setSelectedReviewId(dataItems[0].id);
    }
  }, [viewMode, dataItems, selectedReviewId]);

  const selectedReview = dataItems.find((r: any) => r.id === selectedReviewId);

  // Reset generation section when review changes
  useEffect(() => {
    if (selectedReview) {
      // Приоритет: final_response_text (черновик из списка) > final_text (из responses) > generated_text
      const responseText = (selectedReview as any).final_response_text ||
                          selectedReview.responses?.[0]?.final_text ||
                          selectedReview.responses?.[0]?.generated_text ||
                          selectedReview.generated_response_text ||
                          "";
      setGeneratedText(responseText);
      setOriginalGeneratedText(responseText);
      // Всегда показываем поле ответа — заполненное или пустое для ручного ввода
      setShowGenerateSection(true);
    } else {
      setShowGenerateSection(false);
      setGeneratedText("");
      setOriginalGeneratedText("");
    }
    setSelectedTemplate(undefined);
    setSelectedStyle(undefined);
  // Добавляем generated_response_text в зависимости чтобы обновлять при refetch
  }, [selectedReviewId, selectedReview?.id, selectedReview?.generated_response_text, selectedReview?.final_response_text]);

  // Auto-show generated response in modal
  useEffect(() => {
    if (modalOpen && modalReview) {
      console.log('🔄 [MODAL useEffect] modalReview changed:', modalReview.id);
      // Берем текст: final_text (черновик) > generated_text (из responses) > generated_response_text (из списка)
      // Используем generated_response_text сразу, не ждём загрузки responses
      const responseText = modalReview.responses?.[0]?.final_text ||
                          modalReview.responses?.[0]?.generated_text ||
                          modalReview.generated_response_text ||
                          "";

      setGeneratedText(responseText);
      setOriginalGeneratedText(responseText);
      // Всегда показываем поле ответа — заполненное или пустое для ручного ввода
      setShowGenerateSection(true);
      setSelectedTemplate(undefined);
      setSelectedStyle(undefined);
    }
  }, [modalOpen, modalReview]);

  // Fetch templates
  const templatesQuery = useList({
    resource: "templates",
    filters: [{ field: "is_active", operator: "eq", value: true }],
  });

  const templates = templatesQuery.result?.data || [];

  // Fetch AI styles
  const aiStylesQuery = useList({
    resource: "settings/ai-styles",
    filters: [{ field: "is_active", operator: "eq", value: true }],
  });

  const aiStyles = aiStylesQuery.result?.data || [];

  // Helper: combine text/advantages/disadvantages into single comment
  const getFullComment = (review: any) => {
    const parts = [];
    if (review.advantages) parts.push(`Достоинства: ${review.advantages}`);
    if (review.disadvantages) parts.push(`Недостатки: ${review.disadvantages}`);
    if (review.text) parts.push(review.text);
    return parts.join('\n\n');
  };

  // Navigation between reviews in modal
  const currentModalIndex = modalReview ? dataItems.findIndex((r: any) => r.id === modalReview.id) : -1;
  const hasPrevious = currentModalIndex > 0;
  const hasNext = currentModalIndex >= 0 && currentModalIndex < dataItems.length - 1;

  const hasUnsavedChanges = () => {
    return generatedText !== originalGeneratedText;
  };

  // Helper: объединить данные из списка с данными из API (список имеет приоритет для JOIN-полей)
  const mergeReviewData = (listData: any, apiData: any, responses: any[]) => ({
    ...apiData,
    // Поля из списка (JOIN) имеют приоритет — они уже содержат актуальные данные
    product_photo_url: listData.product_photo_url ?? apiData.product_photo_url,
    seller_sku: listData.seller_sku ?? apiData.seller_sku,
    category: listData.category ?? apiData.category,
    pros_tags: listData.pros_tags ?? apiData.pros_tags,
    cons_tags: listData.cons_tags ?? apiData.cons_tags,
    routing_rule_name: listData.routing_rule_name ?? apiData.routing_rule_name,
    routing_rule_id: listData.routing_rule_id ?? apiData.routing_rule_id,
    routing_mode: listData.routing_mode ?? apiData.routing_mode,
    generated_response_text: listData.generated_response_text ?? apiData.generated_response_text,
    final_response_text: listData.final_response_text ?? apiData.final_response_text,
    response_status: listData.response_status ?? apiData.response_status,
    responses,
  });

  // Helper: загрузить полные данные отзыва и обновить modalReview
  const loadFullReviewData = async (listReview: any) => {
    try {
      const fullData = await api.get<{ review: any; responses: any[] }>(`/reviews/${listReview.id}`);
      setModalReview(mergeReviewData(listReview, fullData.review, fullData.responses));
    } catch (error) {
      console.error('Failed to load review details:', error);
    }
  };

  const navigateToReview = (direction: 'prev' | 'next') => {
    if (!modalReview || currentModalIndex < 0) return;

    const checkAndNavigate = () => {
      const nextIndex = direction === 'prev' ? currentModalIndex - 1 : currentModalIndex + 1;
      if (nextIndex >= 0 && nextIndex < dataItems.length) {
        const nextReview = dataItems[nextIndex];
        // Сразу показываем все данные из списка (включая routing, response text)
        setModalReview(nextReview);
        // Асинхронно догружаем responses (не блокируя UI)
        loadFullReviewData(nextReview);
      }
    };

    if (hasUnsavedChanges()) {
      Modal.confirm({
        title: 'Несохраненные изменения',
        content: 'У вас есть несохраненные изменения в ответе. Продолжить без сохранения?',
        okText: 'Продолжить',
        cancelText: 'Отмена',
        onOk: checkAndNavigate,
      });
    } else {
      checkAndNavigate();
    }
  };

  const handleSaveDraft = async () => {
    console.log('🔍 [SAVE DRAFT] Начало...');
    console.log('🔍 [SAVE DRAFT] modalOpen:', modalOpen);
    console.log('🔍 [SAVE DRAFT] modalReview:', modalReview);
    console.log('🔍 [SAVE DRAFT] selectedReview:', selectedReview);

    const review = modalOpen ? modalReview : selectedReview;
    console.log('🔍 [SAVE DRAFT] Используется review:', review?.id);

    if (!review) {
      console.log('❌ [SAVE DRAFT] Review не найден!');
      return;
    }

    let responseId = review?.responses?.[0]?.id;
    console.log('🔍 [SAVE DRAFT] Initial responseId:', responseId);
    console.log('🔍 [SAVE DRAFT] review.responses:', review?.responses);
    console.log('🔍 [SAVE DRAFT] generatedText length:', generatedText.length);

    // Загрузить response_id если нет
    if (!responseId) {
      console.log('🔍 [SAVE DRAFT] Загрузка полных данных...');
      try {
        const fullData = await api.get<{review: any; responses: any[]}>(`/reviews/${review.id}`);
        console.log('🔍 [SAVE DRAFT] fullData.responses:', fullData.responses);
        responseId = fullData.responses?.[0]?.id;
        console.log('🔍 [SAVE DRAFT] Loaded responseId:', responseId);
        if (modalOpen && fullData.responses?.length > 0) {
          setModalReview(mergeReviewData(review, fullData.review, fullData.responses));
        }
      } catch (e) {
        console.error('❌ [SAVE DRAFT] Ошибка загрузки:', e);
      }
    }

    console.log(`🔍 [SAVE DRAFT] Сохранение final_text (${generatedText.length} символов)...`);

    try {
      let result;
      if (!responseId) {
        // Создаём новый ручной ответ
        console.log('🔍 [SAVE DRAFT] Создание нового ручного ответа...');
        result = await api.post(`/responses/manual`, { review_id: review.id, text: generatedText });
        responseId = result.id;
      } else {
        // Обновляем существующий ответ
        result = await api.patch(`/responses/${responseId}`, { final_text: generatedText });
      }
      console.log('🔍 [SAVE DRAFT] API result:', result);

      setOriginalGeneratedText(generatedText);
      notify.success('Черновик сохранен');
      console.log('✅ [SAVE DRAFT] Уведомление показано');

      // Обновить данные
      if (modalOpen && modalReview) {
        loadFullReviewData(modalReview);
      }

      console.log('🔍 [SAVE DRAFT] Вызов tableQuery.refetch()...');
      tableQuery.refetch();
      console.log('✅ [SAVE DRAFT] Завершено успешно');
    } catch (error: any) {
      console.log('❌ [SAVE DRAFT] Exception:', error);
      notify.error('Ошибка', error.message || String(error));
    }
  };

  const handleApproveResponse = async () => {
    const review = modalOpen ? modalReview : selectedReview;
    if (!review) return;

    let responseId = review?.responses?.[0]?.id;

    // Загрузить response_id если нет
    if (!responseId) {
      const fullData = await api.get<{review: any; responses: any[]}>(`/reviews/${review.id}`);
      responseId = fullData.responses?.[0]?.id;
      if (modalOpen && fullData.responses?.length > 0) {
        setModalReview(mergeReviewData(review, fullData.review, fullData.responses));
      }
    }

    if (!responseId) {
      notify.error('Ошибка', 'Response ID не найден');
      return;
    }

    try {
      await api.post(`/responses/${responseId}/approve`, {});
      setOriginalGeneratedText(generatedText);
      notify.success('Ответ утвержден');

      // Обновить данные
      if (modalOpen && modalReview) {
        loadFullReviewData(modalReview);
      }
      tableQuery.refetch();
    } catch (error: any) {
      notify.error('Ошибка', error.message || String(error));
    }
  };

  const handlePublishResponse = async () => {
    const review = modalOpen ? modalReview : selectedReview;
    if (!review) return;

    let responseId = review?.responses?.[0]?.id;

    // Загрузить response_id если нет
    if (!responseId) {
      const fullData = await api.get<{review: any; responses: any[]}>(`/reviews/${review.id}`);
      responseId = fullData.responses?.[0]?.id;
      if (modalOpen && fullData.responses?.length > 0) {
        setModalReview(mergeReviewData(review, fullData.review, fullData.responses));
      }
    }

    if (!responseId) {
      notify.error('Ошибка', 'Response ID не найден');
      return;
    }

    try {
      // Сначала сохраняем отредактированный текст в БД
      if (generatedText) {
        await api.patch(`/responses/${responseId}`, { final_text: generatedText });
      }

      const result = await api.post<any>(`/responses/${responseId}/publish`, {});
      setOriginalGeneratedText(generatedText);

      // Проверяем статус публикации
      if (result?.status === 'publish_failed') {
        notify.error('Ошибка публикации', result.publish_error || 'Не удалось опубликовать ответ в маркетплейс');
      } else if (result?.already_answered) {
        notify.info('Ответ уже опубликован', result.already_answered_message || 'На этот отзыв уже есть ответ в маркетплейсе');
      } else {
        notify.success('Ответ опубликован');
      }

      // Обновить данные
      if (modalOpen && modalReview) {
        loadFullReviewData(modalReview);
      }
      tableQuery.refetch();
    } catch (error: any) {
      notify.error('Ошибка', error.message || String(error));
    }
  };

  const handleSyncReviews = async () => {
    if (syncMarketplaces.length === 0) {
      notify.error("Ошибка", "Выберите хотя бы один источник");
      return;
    }

    setSyncLoading(true);
    // Единый режим для всех источников: quick=20, medium=50, full=100
    const take = syncMode === "quick" ? 20 : syncMode === "medium" ? 50 : 100;
    const results: string[] = [];

    // Формируем параметры даты
    const dateFrom = syncDateRange?.[0]?.format("DD.MM.YYYY") || "";
    const dateTo = syncDateRange?.[1]?.format("DD.MM.YYYY") || "";

    try {
      for (const mp of syncMarketplaces) {
        if (mp.startsWith("wildberries")) {
          const cabinet = mp.replace("wildberries_", "");
          // Синхронизация отзывов (если выбрано "reviews" или "both")
          if (syncContentType === "reviews" || syncContentType === "both") {
            let url = `${restBaseUrl}/api/v1/wb/import-feedbacks?take=${take}&cabinet=${cabinet}`;
            if (dateFrom) url += `&date_from=${encodeURIComponent(dateFrom)}`;
            if (dateTo) url += `&date_to=${encodeURIComponent(dateTo)}`;
            const response = await fetch(url, { method: "POST" });
            results.push(response.ok ? `WB ${cabinet} отзывы: запущено` : `WB ${cabinet} отзывы: ошибка`);
          }

          // Синхронизация вопросов (если выбрано "questions" или "both")
          if (syncContentType === "questions" || syncContentType === "both") {
            let qUrl = `${restBaseUrl}/api/v1/sync/questions?take=${take}&cabinet=${cabinet}`;
            if (dateFrom) qUrl += `&date_from=${encodeURIComponent(dateFrom)}`;
            if (dateTo) qUrl += `&date_to=${encodeURIComponent(dateTo)}`;
            const qResponse = await fetch(qUrl, { method: "POST" });
            results.push(qResponse.ok ? `WB ${cabinet} вопросы: запущено` : `WB ${cabinet} вопросы: ошибка`);
          }
        } else if (mp === "ozon") {
          // Синхронизация отзывов Ozon (если выбрано "reviews" или "both")
          if (syncContentType === "reviews" || syncContentType === "both") {
            const response = await fetch(
              `${restBaseUrl}/api/v1/ozon/import-feedbacks?mode=${syncMode}`,
              { method: "POST" }
            );
            results.push(response.ok ? "Ozon отзывы: запущено" : "Ozon отзывы: ошибка");
          }

          // Синхронизация вопросов Ozon (если выбрано "questions" или "both")
          if (syncContentType === "questions" || syncContentType === "both") {
            const qResponse = await fetch(
              `${restBaseUrl}/api/v1/sync/ozon-questions?limit=100&status=NEW`,
              { method: "POST" }
            );
            results.push(qResponse.ok ? "Ozon вопросы: запущено" : "Ozon вопросы: ошибка");
          }
        }
      }

      notify.success("Синхронизация запущена", results.join(". "));
      setSyncModalOpen(false);
      setSyncDateRange(null); // Сброс дат после синхронизации
      setSyncContentType("reviews"); // Сброс типа контента
      setTimeout(() => tableQuery.refetch(), 5000);
    } catch (error: any) {
      notify.error("Ошибка синхронизации", error.message);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleGenerate = async () => {
    const review = modalOpen ? modalReview : selectedReview;
    if (!review) return;

    // If template selected, use it
    if (selectedTemplate) {
      const template = templates.find((t: any) => t.id === selectedTemplate);
      if (template) {
        let text = template.content;
        text = text.replace(/\{customer_name\}/g, review.author_name || "Дорогой покупатель");
        text = text.replace(/\{product_name\}/g, review.product_name || "наш продукт");
        text = text.replace(/\{brand\}/g, review.brand || "Art-Visage");
        text = text.replace(/\{product_sku\}/g, review.product_sku || "");
        text = text.replace(/\{resolution_text\}/g, "[Текст решения проблемы]");
        text = text.replace(/\{additional_info\}/g, "[Дополнительная информация]");
        text = text.replace(/\{questions\}/g, "[Вопросы к клиенту]");

        setGeneratedText(text);
        setOriginalGeneratedText(text);
        setShowGenerateSection(true);
        notify.success(`Применен шаблон: ${template.name}`);
        return;
      }
    }

    // Use AI generation with RAG
    try {
      notify.info('Генерация ответа...', 'Пожалуйста, подождите');

      const payload: any = {
        review_id: review.id,
      };

      if (selectedStyle) {
        payload.ai_style_id = selectedStyle;
      }

      const response = await api.post(`/reviews/${review.id}/generate-response`, payload);

      if (response.generated_text) {
        setGeneratedText(response.generated_text);
        setOriginalGeneratedText(response.generated_text);
        setShowGenerateSection(true);
        notify.success('Ответ сгенерирован', 'Текст готов к редактированию');
      } else {
        throw new Error('Нет текста в ответе');
      }
    } catch (error: any) {
      console.log('🔍 [GENERATE ERROR]:', error);
      console.log('🔍 [GENERATE ERROR] message:', error.message);

      // Парсим JSON из сообщения ошибки (формат: "400: {json}")
      let errorDetail = null;
      try {
        const match = error.message?.match(/\d+:\s*(\{.*\})/);
        if (match) {
          errorDetail = JSON.parse(match[1]);
          console.log('🔍 [GENERATE ERROR] parsed detail:', errorDetail);
        }
      } catch (e) {
        console.log('🔍 [GENERATE ERROR] не удалось распарсить JSON');
      }

      // Специальная обработка для необработанного отзыва
      if (errorDetail?.detail?.error === 'review_not_enriched') {
        notify.warning(
          'Отзыв обрабатывается',
          'AI анализ ещё не завершён. Пожалуйста, подождите несколько секунд и попробуйте снова.'
        );
      }
      // Специальная обработка для ручной модерации
      else if (errorDetail?.detail?.error === 'manual_moderation_required') {
        const detail = errorDetail.detail;
        Modal.confirm({
          title: '⚠️ Требуется ручная модерация',
          content: `Отзыв заблокирован правилом "${detail.rule_name}". Вы уверены что хотите сгенерировать ответ?`,
          okText: 'Да, сгенерировать',
          cancelText: 'Отмена',
          onOk: async () => {
            try {
              notify.info('Генерация ответа...', 'Принудительная генерация');
              const review = modalOpen ? modalReview : selectedReview;
              const payload: any = {
                review_id: review!.id,
                force_generate: true
              };
              if (selectedStyle) {
                payload.ai_style_id = selectedStyle;
              }
              const response = await api.post(`/reviews/${review!.id}/generate-response`, payload);
              if (response.generated_text) {
                setGeneratedText(response.generated_text);
                setOriginalGeneratedText(response.generated_text);
                setShowGenerateSection(true);
                notify.success('Ответ сгенерирован', 'Требуется обязательная проверка модератора');
              }
            } catch (err: any) {
              notify.error('Ошибка', err.message || 'Не удалось сгенерировать ответ');
            }
          }
        });
      } else {
        notify.error('Ошибка при генерации ответа', error.message || 'Неизвестная ошибка');
      }
    }
  };

  const renderSource = (source: string) => {
    if (source.startsWith('wildberries')) {
      return <Tag color="purple" style={{ fontWeight: 600 }}>WB</Tag>;
    } else if (source === 'ozon') {
      return <Tag color="blue" style={{ fontWeight: 600 }}>OZON</Tag>;
    } else if (source === 'excel_import') {
      return <Tag color="green" style={{ fontWeight: 600 }}>EXCEL</Tag>;
    }
    return <Tag>{source}</Tag>;
  };

  const renderStatus = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      'new': { label: 'Новый', color: 'blue' },
      'PENDING': { label: 'В обработке AI', color: 'orange' },
      'ENRICHED': { label: 'Обогащен', color: 'cyan' },
      'processing': { label: 'Обработка', color: 'processing' },
      'ready': { label: 'Готов', color: 'green' },
      'moderated': { label: 'Проверен', color: 'cyan' },
      'published': { label: 'Опубликован', color: 'success' },
      'rejected': { label: 'Отклонен', color: 'default' },
      'manual_review': { label: 'Ручная проверка', color: 'red' },
    };

    const config = statusMap[status] || { label: status, color: 'default' };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  // Вычисляем текущий порядок сортировки для колонок
  const getSortOrder = (field: string) => {
    const sorter = sorters?.find((s: any) => s.field === field);
    if (!sorter) return undefined;
    return sorter.order === "asc" ? "ascend" : "descend";
  };

  const columns: ColumnsType<any> = [
    {
      title: "Дата",
      dataIndex: "marketplace_created_at",
      width: 100,
      sorter: true,
      sortOrder: getSortOrder("marketplace_created_at"),
      render: (v, r) => {
        const date = v || r.created_at;
        return date ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 500 }}>{formatLocalDate(date, "DD.MM.YYYY")}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{formatLocalDate(date, "HH:mm")}</div>
          </div>
        ) : "";
      },
    },
    {
      title: "МП",
      dataIndex: "source",
      width: 70,
      render: (source) => {
        if (source === "wildberries_cab1") {
          return <Tag color="purple">WB 1</Tag>;
        } else if (source === "wildberries_cab2") {
          return <Tag color="green">WB 2</Tag>;
        } else if (source === "ozon") {
          return <Tag color="blue">Ozon</Tag>;
        }
        return <Tag>-</Tag>;
      }
    },
    {
      title: "Товар",
      dataIndex: "product_name",
      width: 200,
      render: (name, r) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{
            width: 60,
            height: 60,
            background: '#f5f5f5',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden'
          }}>
            {r.product_photo_url ? (
              <img src={r.product_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ fontSize: 24, color: '#ccc' }}>📦</span>
            )}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 500, marginBottom: 2 }}>
              <Text ellipsis style={{ maxWidth: 180 }}>{name || 'Без названия'}</Text>
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>
              {r.brand || 'Art-Visage'}
            </div>
            <div style={{ fontSize: 11, color: '#999' }}>
              {r.product_sku && <span>Арт: {r.product_sku}</span>}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "★",
      dataIndex: "rating",
      width: 80,
      sorter: true,
      sortOrder: getSortOrder("rating"),
      render: (v, r) => {
        // Для вопросов показываем бейдж вместо рейтинга
        if (r.type === 'question') {
          return <Tag color="blue">Вопрос</Tag>;
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {[1,2,3,4,5].map(i => (
              <StarFilled key={i} style={{ color: i <= v ? '#fadb14' : '#f0f0f0', fontSize: 14 }} />
            ))}
          </div>
        );
      }
    },
    {
      title: "Отзыв / Вопрос",
      dataIndex: "text",
      render: (v, r) => {
        const text = (v || "").toString();
        const isQuestion = r.type === 'question';

        return (
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              {r.author_name || 'Покупатель'}
              {isQuestion && <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>Q&A</Tag>}
            </div>
            {text && (
              <div style={{ marginBottom: 6, color: '#333' }}>
                {text.length > 120 ? text.slice(0, 120) + "…" : text}
              </div>
            )}
            {/* Достоинства */}
            {r.advantages && (
              <div style={{ marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Достоинства: </Text>
                <Text style={{ fontSize: 11, color: '#52c41a' }}>
                  {r.advantages.length > 80 ? r.advantages.slice(0, 80) + "…" : r.advantages}
                </Text>
              </div>
            )}
            {/* Недостатки */}
            {r.disadvantages && (
              <div style={{ marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 10 }}>Недостатки: </Text>
                <Text style={{ fontSize: 11, color: '#ff4d4f' }}>
                  {r.disadvantages.length > 80 ? r.disadvantages.slice(0, 80) + "…" : r.disadvantages}
                </Text>
              </div>
            )}
            {/* Теги из WB API */}
            {r.pros_tags && r.pros_tags.length > 0 && (
              <div style={{ marginBottom: 4 }}>
                {r.pros_tags.slice(0, 4).map((tag: string, i: number) => (
                  <Tag key={i} style={{ fontSize: 11, marginBottom: 2, borderRadius: 10, background: '#e6f7ff', border: '1px solid #91d5ff', color: '#1890ff' }}>{tag}</Tag>
                ))}
                {r.pros_tags.length > 4 && <Tag style={{ fontSize: 11 }}>+{r.pros_tags.length - 4}</Tag>}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: "Статусы",
      dataIndex: "status",
      width: 120,
      render: (status, r) => {
        const responseStatus = r.response_status;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Статус ответа */}
            {responseStatus ? (
              responseStatus === "published" ? (
                <Tag color="green" style={{ margin: 0 }}>Опубликован</Tag>
              ) : responseStatus === "approved" ? (
                <Tag color="cyan" style={{ margin: 0 }}>Утвержден</Tag>
              ) : responseStatus === "draft" ? (
                <Tag color="orange" style={{ margin: 0 }}>Черновик</Tag>
              ) : responseStatus === "publish_failed" ? (
                <Tag color="red" style={{ margin: 0 }}>Ошибка</Tag>
              ) : responseStatus === "pending" ? (
                <Tag color="blue" style={{ margin: 0 }}>Ожидает</Tag>
              ) : (
                <Tag color="default" style={{ margin: 0 }}>{responseStatus}</Tag>
              )
            ) : (
              <Tag color="default" style={{ margin: 0 }}>Нет ответа</Tag>
            )}
            {/* Индикаторы */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {r.has_stop_words && <Tag color="red" style={{ margin: 0, fontSize: 10 }}>⚠️</Tag>}
              {(r.response_source === 'ai' || (!r.response_source && r.routing_mode === 'ai')) && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>AI</Tag>}
              {(r.response_source === 'template' || (!r.response_source && r.routing_mode === 'template')) && <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>Шаблон</Tag>}
              {r.routing_mode === 'manual' && <Tag color="red" style={{ margin: 0, fontSize: 10 }}>Ручной</Tag>}
            </div>
          </div>
        );
      }
    },
    {
      title: "Действия",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, r) => (
        <Space>
          <Tooltip title="Открыть детали">
            <Button
              aria-label="Открыть"
              type="text"
              icon={<EyeOutlined />}
              onClick={async () => {
                // Открываем модалку сразу с данными из списка
                setModalReview(r);
                setModalOpen(true);
                // Асинхронно догружаем responses
                loadFullReviewData(r);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Удалить отзыв"
            description="Вы уверены, что хотите удалить этот отзыв?"
            onConfirm={async () => {
              try {
                await api.delete(`/reviews/${r.id}`);
                notify.success('Отзыв удален');
                tableQuery.refetch();
              } catch (error: any) {
                notify.error('Ошибка при удалении', error.message || 'Не удалось удалить отзыв');
              }
            }}
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Удалить">
              <Button
                aria-label="Удалить"
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const paginationConfig = tableProps.pagination === false ? undefined : tableProps.pagination;
  const currentPage = paginationConfig?.current ?? hookCurrentPage ?? 1;
  const pageSize = paginationConfig?.pageSize ?? 20;
  const totalItems = paginationConfig?.total ?? 0;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;

  // State для массового выбора
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  return (
    <List breadcrumb={false}>
      {/* Верхняя панель как в WB */}
      <div style={{ marginBottom: 16 }}>
        {/* Первая строка: Toggle статуса + Кнопки действий */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Space size="middle">
            <Segmented
              value={responseTab}
              onChange={(value) => handleTabChange(value as string)}
              options={[
                { label: `Ждут ответа (${withoutResponseCount})`, value: 'without_response' },
                { label: `Есть ответ (${withResponseCount})`, value: 'with_response' },
              ]}
            />
            <Segmented
              value={typeTab}
              onChange={(value) => handleTypeTabChange(value as string)}
              options={[
                { label: 'Все', value: 'all' },
                { label: 'Отзывы', value: 'review' },
                { label: 'Вопросы', value: 'question' },
              ]}
            />
            <Space>
              <Switch
                checked={groupByProduct}
                onChange={setGroupByProduct}
                size="small"
              />
              <Text type="secondary">Группировать</Text>
            </Space>
          </Space>
          <Space>
            <Tooltip title={autoSyncEnabled
              ? "Авто-импорт включён: 500 отзывов с каждой площадки ежедневно в 09:00 МСК"
              : "Авто-импорт выключен. Нажмите для включения ежедневной синхронизации в 09:00 МСК"
            }>
              <Button
                icon={<ClockCircleOutlined />}
                type={autoSyncEnabled ? "primary" : "default"}
                loading={autoSyncLoading}
                onClick={async () => {
                  const newValue = !autoSyncEnabled;
                  setAutoSyncLoading(true);
                  try {
                    const resp = await fetch(
                      `${restBaseUrl}/api/v1/settings/auto-sync?enabled=${newValue}`,
                      { method: "PUT" }
                    );
                    if (resp.ok) {
                      setAutoSyncEnabled(newValue);
                      notify.success(
                        newValue ? "Авто-импорт включён" : "Авто-импорт выключен",
                        newValue
                          ? "500 отзывов с каждой площадки ежедневно в 09:00 МСК"
                          : "Автоматическая синхронизация отключена"
                      );
                    } else {
                      notify.error("Ошибка", "Не удалось изменить настройку");
                    }
                  } catch {
                    notify.error("Ошибка", "Сервер недоступен");
                  } finally {
                    setAutoSyncLoading(false);
                  }
                }}
                style={autoSyncEnabled ? {} : { borderStyle: 'dashed' }}
              />
            </Tooltip>
            <Button
              icon={<SyncOutlined />}
              onClick={() => setSyncModalOpen(true)}
              title="Импорт отзывов из маркетплейсов"
            >
              Синхронизация
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                createForm.resetFields();
                setCreateModalOpen(true);
              }}
            >
              Добавить отзыв
            </Button>
          </Space>
        </div>

        {/* Вторая строка: Фильтры как в WB */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <Input
            placeholder="Поиск по Артикулам WB"
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={skuSearch}
            onChange={(e) => setSkuSearch(e.target.value)}
            onPressEnter={() => {
              setFilters([{ field: "product_sku", operator: "eq", value: skuSearch || undefined }], "merge");
              if ((hookCurrentPage ?? 1) !== 1) setCurrentPage(1);
            }}
            style={{ width: 200 }}
            allowClear
            onClear={() => {
              setSkuSearch("");
              setFilters([{ field: "product_sku", operator: "eq", value: undefined }], "merge");
            }}
          />
          <DatePicker.RangePicker
            placeholder={['__.__.____', '__.__.____']}
            format="DD.MM.YYYY"
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates);
              // Передаём даты в формате DD.MM.YYYY для бэкенда
              const dateFrom = dates?.[0]?.format("DD.MM.YYYY") || undefined;
              const dateTo = dates?.[1]?.format("DD.MM.YYYY") || undefined;
              setFilters([
                { field: "date_from", operator: "eq", value: dateFrom },
                { field: "date_to", operator: "eq", value: dateTo },
              ], "merge");
              if ((hookCurrentPage ?? 1) !== 1) setCurrentPage(1);
            }}
            style={{ width: 240 }}
          />
          <Select
            placeholder="Кабинет"
            allowClear
            style={{ width: 140 }}
            onChange={(value) => {
              setFilters([{ field: "source", operator: "eq", value: value || undefined }], "merge");
              if ((hookCurrentPage ?? 1) !== 1) setCurrentPage(1);
            }}
            options={[
              { value: "wildberries_cab1", label: "WB Кабинет 1" },
              { value: "wildberries_cab2", label: "WB Кабинет 2" },
              { value: "ozon", label: "Ozon" },
            ]}
          />
          <Select
            placeholder="Статус ответа"
            allowClear
            style={{ width: 180 }}
            value={responseStatusFilter}
            onChange={(value) => {
              setResponseStatusFilter(value);
              const otherFilters = filters.filter((f: any) => f.field !== 'response_status');
              if (value) {
                // Применяем конкретный статус, не сбрасывая таб
                setFilters([...otherFilters, { field: "response_status", operator: "eq", value }], "replace");
              } else {
                // При сбросе Select восстанавливаем фильтр таба
                if (responseTab === 'with_response') {
                  setFilters([...otherFilters, { field: "response_status", operator: "nnull", value: true }], "replace");
                } else if (responseTab === 'without_response') {
                  setFilters([...otherFilters, { field: "response_status", operator: "null", value: true }], "replace");
                } else {
                  setFilters(otherFilters, "replace");
                }
              }
              if ((hookCurrentPage ?? 1) !== 1) setCurrentPage(1);
            }}
            options={responseStatuses.map(s => ({
              value: s.value,
              label: s.label
            }))}
          />
          <Popover
            trigger="click"
            open={filterPopoverOpen}
            onOpenChange={setFilterPopoverOpen}
            placement="bottomLeft"
            content={
              <div style={{ width: 280 }}>
                {/* Тип отзыва */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 14 }}>📝</span>
                    <Text strong>Тип отзыва</Text>
                  </div>
                  <Radio.Group
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <Space direction="vertical">
                      <Radio value="all">Любой</Radio>
                      <Radio value="with_photo">С фото/видео</Radio>
                    </Space>
                  </Radio.Group>
                </div>

                {/* Оценка отзыва */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 14 }}>⭐</span>
                    <Text strong>Оценка отзыва</Text>
                    {filterRatings.length > 0 && (
                      <span style={{
                        background: '#7B61FF',
                        color: 'white',
                        borderRadius: '50%',
                        width: 8,
                        height: 8,
                        display: 'inline-block'
                      }} />
                    )}
                  </div>
                  <Checkbox.Group
                    value={filterRatings}
                    onChange={(values) => setFilterRatings(values as number[])}
                  >
                    <Space direction="vertical">
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <Checkbox key={rating} value={rating}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {[1, 2, 3, 4, 5].map((i) => (
                              <StarFilled
                                key={i}
                                style={{
                                  color: i <= rating ? '#faad14' : '#f0f0f0',
                                  fontSize: 14,
                                }}
                              />
                            ))}
                          </div>
                        </Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                </div>

                {/* Бренд */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 14 }}>🏷️</span>
                    <Text strong>Бренд</Text>
                    {filterBrands.length > 0 && (
                      <span style={{
                        background: '#7B61FF',
                        color: 'white',
                        borderRadius: '50%',
                        width: 8,
                        height: 8,
                        display: 'inline-block'
                      }} />
                    )}
                  </div>
                  <Select
                    mode="multiple"
                    placeholder="Выберите бренды"
                    value={filterBrands}
                    onChange={(values) => setFilterBrands(values)}
                    style={{ width: '100%' }}
                    allowClear
                    maxTagCount={2}
                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                  >
                    {availableBrands.map((brand) => (
                      <Select.Option key={brand} value={brand}>
                        {brand}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {/* Кнопки */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    type="primary"
                    style={{ background: '#7B61FF' }}
                    onClick={() => {
                      // Применить фильтры
                      const newFilters: any[] = [];

                      // Фильтр по типу (с фото)
                      if (filterType === 'with_photo') {
                        // TODO: добавить поле has_photos в БД и API
                      }

                      // Фильтр по рейтингу (передаём как строку через запятую)
                      if (filterRatings.length > 0 && filterRatings.length < 5) {
                        newFilters.push({
                          field: "rating",
                          operator: "eq",
                          value: filterRatings.join(",")
                        });
                      } else {
                        // Сбросить фильтр по рейтингу
                        newFilters.push({
                          field: "rating",
                          operator: "eq",
                          value: undefined
                        });
                      }

                      // Фильтр по бренду (передаём как строку через запятую)
                      if (filterBrands.length > 0) {
                        newFilters.push({
                          field: "brand",
                          operator: "eq",
                          value: filterBrands.join(",")
                        });
                      } else {
                        newFilters.push({
                          field: "brand",
                          operator: "eq",
                          value: undefined
                        });
                      }

                      setFilters(newFilters, "merge");
                      if ((hookCurrentPage ?? 1) !== 1) setCurrentPage(1);
                      setFilterPopoverOpen(false);
                    }}
                  >
                    Применить
                  </Button>
                  <Button
                    onClick={() => {
                      setFilterType('all');
                      setFilterRatings([]);
                      setFilterBrands([]);
                      setFilters([
                        { field: "rating", operator: "eq", value: undefined },
                        { field: "brand", operator: "eq", value: undefined }
                      ], "merge");
                      setFilterPopoverOpen(false);
                    }}
                  >
                    Сбросить
                  </Button>
                </div>
              </div>
            }
          >
            <Button
              icon={<FilterOutlined />}
              style={filterRatings.length > 0 || filterType !== 'all' || filterBrands.length > 0 ? { borderColor: '#7B61FF', color: '#7B61FF' } : {}}
            >
              Фильтры
              {(filterRatings.length > 0 || filterBrands.length > 0) && (
                <span style={{ marginLeft: 4, background: '#7B61FF', color: 'white', borderRadius: 10, padding: '0 6px', fontSize: 11 }}>
                  {filterRatings.length + filterBrands.length}
                </span>
              )}
            </Button>
          </Popover>
        </div>

        {/* Третья строка: Toolbar для выбранных */}
        {selectedRowKeys.length > 0 && (
          <div style={{
            background: '#e6f4ff',
            padding: '8px 12px',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <Text strong>Выбрано: {selectedRowKeys.length}</Text>
            <Button size="small" type="primary" onClick={() => setBulkGenerateModalOpen(true)}>Сгенерировать ответы</Button>
            <Button size="small" onClick={() => setExportModalOpen(true)}>Экспорт</Button>
            <Button size="small" onClick={() => setSelectedRowKeys([])}>Отменить</Button>
          </div>
        )}
      </div>
      {viewMode === 'table' ? (
        groupByProduct && groupedData ? (
          // Accordion view для группировки по товару
          <Collapse accordion>
            {groupedData.map((group: any) => (
              <Collapse.Panel
                key={group.id}
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}>
                    <input
                      type="checkbox"
                      checked={group.children?.every((r: any) => selectedRowKeys.includes(r.id))}
                      onChange={(e) => {
                        e.stopPropagation();
                        const childIds = group.children?.map((r: any) => r.id) || [];
                        if (e.target.checked) {
                          setSelectedRowKeys([...new Set([...selectedRowKeys, ...childIds])]);
                        } else {
                          setSelectedRowKeys(selectedRowKeys.filter(k => !childIds.includes(k)));
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: 16, height: 16 }}
                    />
                    <div style={{
                      width: 60,
                      height: 60,
                      background: '#f5f5f5',
                      borderRadius: 4,
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {group.product_photo_url ? (
                        <img src={group.product_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 24, color: '#ccc' }}>📦</span>
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#1890ff', marginBottom: 2 }}>
                        {group.product_name || 'Без названия'}
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {group.category ? `${group.category} · ` : ''}{group.brand || 'Art-Visage'}
                      </div>
                      <div style={{ fontSize: 11, color: '#999' }}>
                        Арт. продавца: {group.seller_sku || group.product_sku || '—'}
                        {group.product_sku && <span style={{ marginLeft: 8 }}>Артикул WB: {group.product_sku}</span>}
                      </div>
                    </div>
                    <Tag color="blue">{group.reviewCount} отзыв(ов)</Tag>
                  </div>
                }
              >
                {/* Список отзывов внутри группы */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {group.children?.map((review: any) => (
                    <div key={review.id} style={{
                      display: 'flex',
                      gap: 16,
                      padding: 12,
                      background: '#fafafa',
                      borderRadius: 8,
                      border: '1px solid #f0f0f0'
                    }}>
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedRowKeys.includes(review.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRowKeys([...selectedRowKeys, review.id]);
                          } else {
                            setSelectedRowKeys(selectedRowKeys.filter(k => k !== review.id));
                          }
                        }}
                        style={{ width: 16, height: 16, flexShrink: 0, marginTop: 4 }}
                      />

                      {/* Дата и время (из WB) */}
                      <div style={{ width: 80, flexShrink: 0, textAlign: 'center' }}>
                        <div style={{ fontWeight: 500, color: '#1890ff' }}>
                          {formatLocalDate(review.marketplace_created_at || review.created_at, 'DD.MM.YYYY')}
                        </div>
                        <div style={{ fontSize: 12, color: '#888' }}>
                          {formatLocalDate(review.marketplace_created_at || review.created_at, 'HH:mm')}
                        </div>
                      </div>

                      {/* Рейтинг */}
                      <div style={{ width: 80, flexShrink: 0 }}>
                        {[1,2,3,4,5].map(i => (
                          <StarFilled key={i} style={{ color: i <= review.rating ? '#fadb14' : '#f0f0f0', fontSize: 12 }} />
                        ))}
                      </div>

                      {/* Отзыв */}
                      <div style={{ flex: 1 }}>
                        {/* Автор и мета */}
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ fontWeight: 600 }}>{review.author_name || 'Покупатель'}</span>
                          <span style={{ color: '#999', fontSize: 12, marginLeft: 12 }}>
                            {review.marketplace_created_at && `Покупка: ${formatLocalDate(review.marketplace_created_at, 'DD.MM.YYYY')}`}
                            {review.color && ` · Цвет: ${review.color}`}
                            {review.barcode && ` · ШК: ${review.barcode}`}
                            {review.external_id && ` · ID: ${review.external_id}`}
                          </span>
                        </div>

                        {/* Текст отзыва */}
                        {review.text && (
                          <div style={{ marginBottom: 8, color: '#333' }}>
                            {review.text}
                          </div>
                        )}

                        {/* Преимущества (текст) */}
                        {review.advantages && (
                          <div style={{ marginBottom: 6 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>Достоинства: </Text>
                            <Text style={{ fontSize: 12, color: '#52c41a' }}>{review.advantages}</Text>
                          </div>
                        )}

                        {/* Недостатки (текст) */}
                        {review.disadvantages && (
                          <div style={{ marginBottom: 6 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>Недостатки: </Text>
                            <Text style={{ fontSize: 12, color: '#ff4d4f' }}>{review.disadvantages}</Text>
                          </div>
                        )}

                        {/* Теги преимуществ */}
                        {review.pros_tags && review.pros_tags.length > 0 && (
                          <div style={{ marginBottom: 4 }}>
                            {review.pros_tags.map((tag: string, i: number) => (
                              <Tag
                                key={i}
                                style={{
                                  borderRadius: 12,
                                  background: '#e6f7ff',
                                  border: '1px solid #91d5ff',
                                  color: '#1890ff',
                                  fontSize: 11,
                                  margin: '2px 4px 2px 0'
                                }}
                              >
                                {tag}
                              </Tag>
                            ))}
                          </div>
                        )}

                        {/* Теги недостатков */}
                        {review.cons_tags && review.cons_tags.length > 0 && (
                          <div>
                            {review.cons_tags.map((tag: string, i: number) => (
                              <Tag
                                key={i}
                                style={{
                                  borderRadius: 12,
                                  background: '#fff1f0',
                                  border: '1px solid #ffa39e',
                                  color: '#ff4d4f',
                                  fontSize: 11,
                                  margin: '2px 4px 2px 0'
                                }}
                              >
                                {tag}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Статусы и действия */}
                      <div style={{ width: 140, flexShrink: 0, textAlign: 'right' }}>
                        <Space direction="vertical" size={4}>
                          {review.response_status ? (
                            review.response_status === "published" ? (
                              <Tag color="green">Опубликован</Tag>
                            ) : review.response_status === "approved" ? (
                              <Tag color="cyan">Утвержден</Tag>
                            ) : (
                              <Tag color="orange">Черновик</Tag>
                            )
                          ) : (
                            <Tag color="default">Нет ответа</Tag>
                          )}
                          <Space size={4}>
                            <Tooltip title="Открыть">
                              <Button
                                size="small"
                                type="text"
                                icon={<EyeOutlined />}
                                onClick={() => {
                                  setModalReview(review);
                                  setModalOpen(true);
                                  loadFullReviewData(review);
                                }}
                              />
                            </Tooltip>
                            <Popconfirm
                              title="Удалить отзыв?"
                              onConfirm={async () => {
                                try {
                                  await api.delete(`/reviews/${review.id}`);
                                  notify.success('Отзыв удален');
                                  tableQuery.refetch();
                                } catch (error: any) {
                                  notify.error('Ошибка', error.message);
                                }
                              }}
                              okText="Да"
                              cancelText="Нет"
                            >
                              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          </Space>
                        </Space>
                      </div>
                    </div>
                  ))}
                </div>
              </Collapse.Panel>
            ))}
          </Collapse>
        ) : (
          // Обычная таблица
          <Table
            {...tableProps}
            rowKey="id"
            columns={columns}
            rowSelection={rowSelection}
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              locale: { items_per_page: " записей на странице" },
              showTotal: (total, range) => (total === 0 ? "Нет отзывов" : `Страница ${currentPage} из ${totalPages} · ${range[0]}-${range[1]} из ${total}`),
            }}
          />
        )
      ) : (
        <div className="stream-container" style={{ display: 'flex', height: 'calc(100vh - 240px)', position: 'relative' }}>
          {/* Left panel: Review cards list */}
          <div style={{ width: `${leftPanelWidth}%`, overflowY: 'auto', paddingRight: 8 }}>
            <AntList
              dataSource={dataItems}
              renderItem={(review: any) => (
                <Card
                  key={review.id}
                  size="small"
                  hoverable
                  onClick={() => setSelectedReviewId(review.id)}
                  style={{
                    marginBottom: 12,
                    cursor: 'pointer',
                    border: selectedReviewId === review.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                    background: selectedReviewId === review.id ? '#f0f8ff' : 'white',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Space>
                      {renderSource(review.source)}
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatLocalDate(review.created_at)}
                      </Text>
                    </Space>
                    <Space>
                      <StarFilled style={{ color: '#faad14' }} />
                      <Text strong>{review.rating}</Text>
                    </Space>
                  </div>
                  <Text strong style={{ fontSize: 13 }}>{review.product_name}</Text>
                  <Paragraph
                    ellipsis={{ rows: 2 }}
                    style={{ marginTop: 8, marginBottom: 8, fontSize: 12 }}
                  >
                    {review.text || review.advantages || review.disadvantages || ''}
                  </Paragraph>
                  <Space size={4} wrap>
                    {renderStatus(review.status)}
                    {review.routing_rule_name && (
                      <Tooltip title={`Маршрут: ${review.routing_rule_name}`}>
                        <Tag
                          color={
                            review.routing_mode === 'manual' ? 'orange' :
                            review.routing_mode === 'template' ? 'blue' :
                            'green'
                          }
                          style={{ fontSize: 11 }}
                        >
                          {review.routing_rule_name.length > 15
                            ? review.routing_rule_name.substring(0, 15) + '...'
                            : review.routing_rule_name}
                        </Tag>
                      </Tooltip>
                    )}
                    {review.routing_mode === 'manual' && (
                      <Tag color="red" style={{ fontSize: 11 }}>Ручной</Tag>
                    )}
                    {review.response_status && (
                      <Tag
                        color={RESPONSE_STATUSES[review.response_status]?.color || 'default'}
                        style={{ fontSize: 11 }}
                      >
                        {RESPONSE_STATUSES[review.response_status]?.icon} {RESPONSE_STATUSES[review.response_status]?.label || review.response_status}
                      </Tag>
                    )}
                    {review.has_stop_words && <Tag color="red" style={{ fontSize: 11 }}>Стоп-слова</Tag>}
                  </Space>
                </Card>
              )}
            />
          </div>

          {/* Resizable divider */}
          <div
            onMouseDown={handleMouseDown}
            style={{
              width: '8px',
              cursor: 'col-resize',
              background: isResizing ? '#1890ff' : '#d9d9d9',
              transition: isResizing ? 'none' : 'background 0.3s',
              flexShrink: 0,
              position: 'relative',
              userSelect: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '2px',
                height: '40px',
                background: 'white',
                borderRadius: '2px',
              }}
            />
          </div>

          {/* Right panel: Selected review details */}
          <div style={{ width: `${100 - leftPanelWidth}%`, overflowY: 'auto', paddingLeft: 8 }}>
            {selectedReview ? (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {/* Review details card */}
                <Card title="Отзыв" size="small">
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Дата">
                      {formatLocalDate(selectedReview.created_at)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Источник">
                      {renderSource(selectedReview.source)}
                    </Descriptions.Item>
                    <Descriptions.Item label="SKU">{selectedReview.product_sku}</Descriptions.Item>
                    <Descriptions.Item label="Товар" span={2}>{selectedReview.product_name}</Descriptions.Item>
                    <Descriptions.Item label="Рейтинг">
                      <Space>
                        <StarFilled style={{ color: '#faad14' }} />
                        <Text strong>{selectedReview.rating}</Text>
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Статус">
                      <Space>
                        {renderStatus(selectedReview.status)}
                        {selectedReview.has_stop_words && <Tag color="red" style={{ fontSize: 11 }}>Стоп-слова</Tag>}
                      </Space>
                    </Descriptions.Item>
                    {selectedReview.sentiment && (
                      <Descriptions.Item label="Тональность">
                        <Tag color={selectedReview.sentiment === 'positive' ? 'green' : selectedReview.sentiment === 'negative' ? 'red' : 'default'}>
                          {selectedReview.sentiment === 'positive' ? 'Позитивная' : selectedReview.sentiment === 'negative' ? 'Негативная' : 'Нейтральная'}
                        </Tag>
                      </Descriptions.Item>
                    )}
                    {selectedReview.topics && (
                      <Descriptions.Item label="Темы" span={selectedReview.sentiment ? 1 : 2}>
                        <Space wrap>
                          {Array.isArray(selectedReview.topics?.topics)
                            ? selectedReview.topics.topics.map((topic: string, i: number) => (
                                <Tag key={i} color="blue">{topic}</Tag>
                              ))
                            : Array.isArray(selectedReview.topics)
                            ? selectedReview.topics.map((topic: string, i: number) => (
                                <Tag key={i} color="blue">{topic}</Tag>
                              ))
                            : <Text type="secondary">-</Text>
                          }
                        </Space>
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  {getFullComment(selectedReview) && (
                    <>
                      <Divider style={{ margin: '12px 0' }} />
                      <div>
                        <Text strong style={{ fontSize: 13 }}>Комментарий:</Text>
                        <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                          {getFullComment(selectedReview)}
                        </Paragraph>
                      </div>
                    </>
                  )}

                  {modalReview?.responses?.[0]?.rag_sources?.routing_rule && (
                    <>
                      <Divider style={{ margin: '12px 0' }} />
                      <div>
                        <Text strong style={{ fontSize: 13 }}>Маршрут:</Text>
                        <div style={{ marginTop: 8 }}>
                          <Space>
                            <Text strong>{modalReview.responses[0].rag_sources.routing_rule.rule_name}</Text>
                            <Tag color={modalReview.responses[0].rag_sources.routing_rule.mode === 'template' ? 'blue' : modalReview.responses[0].rag_sources.routing_rule.mode === 'ai' ? 'green' : 'orange'}>
                              {modalReview.responses[0].rag_sources.routing_rule.mode}
                            </Tag>
                            {modalReview.responses[0].rag_sources.routing_rule.reasons && modalReview.responses[0].rag_sources.routing_rule.reasons.length > 0 && (
                              <Text type="secondary">({modalReview.responses[0].rag_sources.routing_rule.reasons.join(', ')})</Text>
                            )}
                          </Space>
                        </div>
                      </div>
                    </>
                  )}
                </Card>

                {/* Generation settings */}
                <Card title="Генерация ответа" size="small">
                  <Space direction="vertical" style={{ width: '100%' }} size={12}>
                    <Space wrap>
                      <Select
                        allowClear
                        placeholder="Выбрать шаблон"
                        style={{ width: 280 }}
                        value={selectedTemplate}
                        onChange={setSelectedTemplate}
                        optionFilterProp="label"
                        showSearch
                        options={templates.map((t: any) => {
                          // Check if template is recommended for this review
                          const recommended =
                            t.recommended_for?.sentiment?.includes(selectedReview?.sentiment) ||
                            t.recommended_for?.rating?.includes(selectedReview?.rating);

                          return {
                            value: t.id,
                            label: (
                              <div>
                                {recommended && <span style={{ color: '#faad14' }}>⭐ </span>}
                                {t.name}
                                {t.category && <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>{t.category}</Tag>}
                              </div>
                            ),
                            searchLabel: t.name,
                            recommended,
                          };
                        }).sort((a, b) => {
                          // Sort: recommended first
                          if (a.recommended && !b.recommended) return -1;
                          if (!a.recommended && b.recommended) return 1;
                          return 0;
                        })}
                      />
                      <Select
                        allowClear
                        placeholder="Стиль ответа"
                        style={{ width: 200 }}
                        value={selectedStyle}
                        onChange={setSelectedStyle}
                        options={aiStyles.map((s: any) => ({ value: s.id, label: s.style }))}
                      />
                    </Space>
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={handleGenerate}
                      block
                    >
                      {selectedReview?.generated_response_text ? 'Перегенерировать ответ' : 'Сгенерировать ответ'}
                    </Button>
                  </Space>
                </Card>

                {/* Generated response */}
                {showGenerateSection && (
                  <Card
                    title={
                      <Space>
                        <span>{generatedText ? 'Сгенерированный ответ' : 'Ответ'}</span>
                        {(() => {
                          const status = selectedReview?.responses?.[0]?.status || selectedReview?.response_status;
                          const config = RESPONSE_STATUSES[status] || RESPONSE_STATUSES.draft;
                          return <Tag color={config.color}>{config.icon} {config.label}</Tag>;
                        })()}
                      </Space>
                    }
                    size="small"
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                      <TextArea
                        value={generatedText}
                        onChange={(e) => setGeneratedText(e.target.value)}
                        rows={8}
                        placeholder="Введите текст ответа..."
                        disabled={(selectedReview?.responses?.[0]?.status || selectedReview?.response_status) === 'published'}
                      />
                      <Space wrap>
                        <Button
                          type="default"
                          onClick={handleSaveDraft}
                          disabled={(selectedReview?.responses?.[0]?.status || selectedReview?.response_status) === 'published'}
                        >
                          Сохранить черновик
                        </Button>
                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={handleApproveResponse}
                          disabled={(selectedReview?.responses?.[0]?.status || selectedReview?.response_status) === 'published'}
                        >
                          Утвердить
                        </Button>
                        <Button
                          onClick={handlePublishResponse}
                          disabled={(selectedReview?.responses?.[0]?.status || selectedReview?.response_status) === 'published'}
                        >
                          Опубликовать
                        </Button>
                        <Button
                          icon={<CloseOutlined />}
                          onClick={() => {
                            setShowGenerateSection(false);
                            setGeneratedText("");
                          }}
                        >
                          Отменить
                        </Button>
                      </Space>
                    </Space>
                  </Card>
                )}
              </Space>
            ) : (
              <Card>
                <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                  Выберите отзыв из списка слева
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Modal for review details */}
      <Modal
        title={
          <Space>
            <span>Отзыв</span>
            {modalReview && renderSource(modalReview.source)}
            {modalReview?.has_stop_words && <Tag color="red">Стоп-слова</Tag>}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {currentModalIndex >= 0 && `(${currentModalIndex + 1} из ${dataItems.length})`}
            </Text>
          </Space>
        }
        open={modalOpen}
        onCancel={() => {
          const closeModal = () => {
            setModalOpen(false);
            setModalReview(null);
            setShowGenerateSection(false);
            setGeneratedText("");
            setOriginalGeneratedText("");
            setSelectedTemplate(undefined);
            setSelectedStyle(undefined);
          };

          if (hasUnsavedChanges()) {
            Modal.confirm({
              title: 'Несохраненные изменения',
              content: 'У вас есть несохраненные изменения в ответе. Закрыть без сохранения?',
              okText: 'Закрыть без сохранения',
              cancelText: 'Отмена',
              okButtonProps: { danger: true },
              onOk: closeModal,
            });
          } else {
            closeModal();
          }
        }}
        width={900}
        footer={null}
      >
        {modalReview && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* Navigation buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                disabled={!hasPrevious}
                onClick={() => navigateToReview('prev')}
              >
                &lt; Предыдущий
              </Button>
              <Button
                disabled={!hasNext}
                onClick={() => navigateToReview('next')}
              >
                Следующий &gt;
              </Button>
            </div>

            {/* Review details - Compact horizontal layout */}
            <Card size="small">
              <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                {/* Product photo - small */}
                <div style={{
                  width: 60,
                  height: 60,
                  background: '#f5f5f5',
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden'
                }}>
                  {modalReview.product_photo_url ? (
                    <img
                      src={modalReview.product_photo_url}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).parentElement!.innerHTML = '<span style="font-size: 28px; color: #ccc;">📦</span>';
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: 28, color: '#ccc' }}>📦</span>
                  )}
                </div>

                {/* Compact info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Product name + brand + category */}
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, lineHeight: 1.3 }}>
                    {modalReview.product_name || 'Без названия'}
                    {modalReview.brand && (
                      <Text type="secondary" style={{ fontSize: 13, fontWeight: 400, marginLeft: 8 }}>
                        · {modalReview.brand}
                      </Text>
                    )}
                    {modalReview.category && (
                      <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 6 }}>
                        · {modalReview.category}
                      </Text>
                    )}
                  </div>

                  {/* Author + date + rating */}
                  <div style={{ marginBottom: 6 }}>
                    <Text strong style={{ fontSize: 13 }}>{modalReview.author_name || 'Покупатель'}</Text>
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
                      {modalReview.marketplace_created_at
                        ? formatLocalDate(modalReview.marketplace_created_at)
                        : formatLocalDate(modalReview.created_at)}
                    </Text>
                    <span style={{ marginLeft: 12 }}>
                      {[1,2,3,4,5].map(i => (
                        <StarFilled key={i} style={{ color: i <= modalReview.rating ? '#fadb14' : '#f0f0f0', fontSize: 13 }} />
                      ))}
                    </span>
                  </div>

                  {/* SKU + seller_sku */}
                  <div style={{ fontSize: 11, color: '#999' }}>
                    Арт. WB: <Text code style={{ fontSize: 11 }}>{modalReview.product_sku}</Text>
                    {modalReview.seller_sku && (
                      <>
                        {' · '}Арт. продавца: <Text code style={{ fontSize: 11 }}>{modalReview.seller_sku}</Text>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Technical details - Collapse */}
              <Collapse
                ghost
                size="small"
                items={[{
                  key: 'tech',
                  label: <Text type="secondary" style={{ fontSize: 12 }}>Технические данные</Text>,
                  children: (
                    <Space direction="vertical" size={4} style={{ width: '100%', fontSize: 12 }}>
                      {modalReview.color && <div><Text type="secondary">Цвет:</Text> {modalReview.color}</div>}
                      {modalReview.size && <div><Text type="secondary">Размер:</Text> {modalReview.size}</div>}
                      {modalReview.barcode && <div><Text type="secondary">Штрих-код:</Text> <Text code>{modalReview.barcode}</Text></div>}
                      {modalReview.external_id && <div><Text type="secondary">ID отзыва на МП:</Text> <Text code>{modalReview.external_id}</Text></div>}
                      {modalReview.author_region && <div><Text type="secondary">Регион:</Text> {modalReview.author_region}</div>}
                      <div><Text type="secondary">Дата в БД:</Text> {formatLocalDate(modalReview.created_at)}</div>
                    </Space>
                  )
                }]}
              />

              <Divider style={{ margin: '12px 0' }} />

              {/* AI processing - ultra compact */}
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {/* Routing + Status in one line */}
                {modalReview.routing_rule && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Маршрут: </Text>
                    <Text strong style={{ fontSize: 13 }}>{modalReview.routing_rule.rule_name}</Text>
                    {' '}
                    <Tag color={modalReview.routing_rule.mode === 'template' ? 'blue' : modalReview.routing_rule.mode === 'ai' ? 'green' : 'orange'} style={{ fontSize: 11 }}>
                      {modalReview.routing_rule.mode === 'manual' ? 'Ручной' : modalReview.routing_rule.mode === 'template' ? 'Шаблон' : 'AI'}
                    </Tag>
                    {' '}
                    {renderStatus(modalReview.status)}
                  </div>
                )}

                {/* Sentiment + Topics in one line */}
                {(modalReview.sentiment || modalReview.topics) && (
                  <div>
                    {modalReview.sentiment && (
                      <>
                        <Text type="secondary" style={{ fontSize: 12 }}>Тональность: </Text>
                        <Tag color={modalReview.sentiment === 'positive' ? 'green' : modalReview.sentiment === 'negative' ? 'red' : 'default'} style={{ fontSize: 11 }}>
                          {modalReview.sentiment === 'positive' ? 'Позитивная' : modalReview.sentiment === 'negative' ? 'Негативная' : 'Нейтральная'}
                        </Tag>
                      </>
                    )}
                    {modalReview.topics && (
                      <>
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: modalReview.sentiment ? 16 : 0 }}>Темы: </Text>
                        {Array.isArray(modalReview.topics?.topics)
                          ? modalReview.topics.topics.map((topic: string, i: number) => (
                              <Tag key={i} color="blue" style={{ fontSize: 11 }}>{topic}</Tag>
                            ))
                          : Array.isArray(modalReview.topics)
                          ? modalReview.topics.map((topic: string, i: number) => (
                              <Tag key={i} color="blue" style={{ fontSize: 11 }}>{topic}</Tag>
                            ))
                          : null
                        }
                      </>
                    )}
                  </div>
                )}
              </Space>

              {getFullComment(modalReview) && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <div>
                    <Text strong style={{ fontSize: 13 }}>Комментарий:</Text>
                    <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                      {getFullComment(modalReview)}
                    </Paragraph>
                  </div>

                  {/* Pros tags */}
                  {modalReview.pros_tags && modalReview.pros_tags.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <Text strong style={{ fontSize: 13 }}>✅ Преимущества (теги):</Text>
                      <div style={{ marginTop: 6 }}>
                        <Space wrap size={[4, 6]}>
                          {modalReview.pros_tags.map((tag: string, i: number) => (
                            <Tag
                              key={i}
                              style={{
                                borderRadius: 12,
                                background: '#e6f7ff',
                                border: '1px solid #91d5ff',
                                color: '#1890ff',
                                fontSize: 12,
                                padding: '2px 10px'
                              }}
                            >
                              {tag}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    </div>
                  )}

                  {/* Cons tags */}
                  {modalReview.cons_tags && modalReview.cons_tags.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <Text strong style={{ fontSize: 13 }}>❌ Недостатки (теги):</Text>
                      <div style={{ marginTop: 6 }}>
                        <Space wrap size={[4, 6]}>
                          {modalReview.cons_tags.map((tag: string, i: number) => (
                            <Tag
                              key={i}
                              style={{
                                borderRadius: 12,
                                background: '#fff1f0',
                                border: '1px solid #ffa39e',
                                color: '#ff4d4f',
                                fontSize: 12,
                                padding: '2px 10px'
                              }}
                            >
                              {tag}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>

            {/* Generation settings */}
            <Card title="Генерация ответа" size="small">
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <Space wrap>
                  <Select
                    allowClear
                    placeholder="Выбрать шаблон"
                    style={{ width: 280 }}
                    value={selectedTemplate}
                    onChange={setSelectedTemplate}
                    optionFilterProp="searchLabel"
                    showSearch
                    options={templates.map((t: any) => {
                      // Check if template is recommended for this review
                      const recommended =
                        t.recommended_for?.sentiment?.includes(modalReview?.sentiment) ||
                        t.recommended_for?.rating?.includes(modalReview?.rating);

                      return {
                        value: t.id,
                        label: (
                          <div>
                            {recommended && <span style={{ color: '#faad14' }}>⭐ </span>}
                            {t.name}
                            {t.category && <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>{t.category}</Tag>}
                          </div>
                        ),
                        searchLabel: t.name,
                        recommended,
                      };
                    }).sort((a, b) => {
                      // Sort: recommended first
                      if (a.recommended && !b.recommended) return -1;
                      if (!a.recommended && b.recommended) return 1;
                      return 0;
                    })}
                  />
                  <Select
                    allowClear
                    placeholder="Стиль ответа"
                    style={{ width: 200 }}
                    value={selectedStyle}
                    onChange={setSelectedStyle}
                    options={aiStyles.map((s: any) => ({ value: s.id, label: s.style }))}
                  />
                </Space>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleGenerate}
                  block
                >
                  {modalReview?.generated_response_text ? 'Перегенерировать ответ' : 'Сгенерировать ответ'}
                </Button>
              </Space>
            </Card>

            {/* Generated response */}
            {showGenerateSection && (
              <Card
                title={
                  <Space>
                    <span>{generatedText ? 'Сгенерированный ответ' : 'Ответ'}</span>
                    {(() => {
                      const status = modalReview?.responses?.[0]?.status || modalReview?.response_status;
                      const config = RESPONSE_STATUSES[status] || RESPONSE_STATUSES.draft;
                      return <Tag color={config.color}>{config.icon} {config.label}</Tag>;
                    })()}
                  </Space>
                }
                size="small"
              >
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  <TextArea
                    value={generatedText}
                    onChange={(e) => setGeneratedText(e.target.value)}
                    rows={8}
                    placeholder="Введите текст ответа..."
                    disabled={(modalReview?.responses?.[0]?.status || modalReview?.response_status) === 'published'}
                  />
                  <Space wrap>
                    <Button
                      type="default"
                      onClick={handleSaveDraft}
                      disabled={(modalReview?.responses?.[0]?.status || modalReview?.response_status) === 'published'}
                    >
                      Сохранить черновик
                    </Button>
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={handleApproveResponse}
                      disabled={(modalReview?.responses?.[0]?.status || modalReview?.response_status) === 'published'}
                    >
                      Утвердить
                    </Button>
                    <Button
                      onClick={handlePublishResponse}
                      disabled={(modalReview?.responses?.[0]?.status || modalReview?.response_status) === 'published'}
                    >
                      Опубликовать
                    </Button>
                  </Space>
                </Space>
              </Card>
            )}
          </Space>
        )}
      </Modal>

      {/* Sync reviews modal */}
      <Modal
        title="Синхронизация отзывов из маркетплейсов"
        open={syncModalOpen}
        onOk={handleSyncReviews}
        onCancel={() => setSyncModalOpen(false)}
        confirmLoading={syncLoading}
        okText="Запустить"
        cancelText="Отмена"
        width={500}
      >
        <Alert
          message="Импорт отзывов"
          description="Загружаются только отзывы без ответа. Дубликаты автоматически пропускаются."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />

        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Режим:</Text>
          <Radio.Group value={syncMode} onChange={(e) => setSyncMode(e.target.value)}>
            <Space direction="vertical">
              <Radio value="quick">Быстрый (20 отзывов)</Radio>
              <Radio value="medium">Средний (50 отзывов)</Radio>
              <Radio value="full">Полный (100 отзывов)</Radio>
            </Space>
          </Radio.Group>
        </div>

        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Период (опционально):</Text>
          <DatePicker.RangePicker
            format="DD.MM.YYYY"
            value={syncDateRange}
            onChange={(dates) => setSyncDateRange(dates)}
            placeholder={["С даты", "По дату"]}
            style={{ width: '100%' }}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
            Оставьте пустым для загрузки последних отзывов
          </Text>
        </div>

        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Источники:</Text>
          <Checkbox.Group value={syncMarketplaces} onChange={(v) => setSyncMarketplaces(v as string[])}>
            <Space direction="vertical">
              <Checkbox value="wildberries_cab1"><Badge color="purple" text="WB Кабинет 1" /></Checkbox>
              <Checkbox value="wildberries_cab2"><Badge color="purple" text="WB Кабинет 2" /></Checkbox>
              <Checkbox value="ozon"><Badge color="blue" text="Ozon" /></Checkbox>
            </Space>
          </Checkbox.Group>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Тип контента:</Text>
          <Select
            value={syncContentType}
            onChange={(v) => setSyncContentType(v)}
            style={{ width: '100%' }}
            options={[
              { value: 'reviews', label: '📝 Только отзывы' },
              { value: 'questions', label: '❓ Только вопросы (Q&A)' },
              { value: 'both', label: '📝❓ Отзывы и вопросы' },
            ]}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
            {syncContentType === 'reviews' && 'Импортирует только отзывы покупателей'}
            {syncContentType === 'questions' && 'Импортирует только вопросы без ответов (WB Questions API / Ozon Questions API)'}
            {syncContentType === 'both' && 'Импортирует отзывы и вопросы покупателей'}
          </Text>
        </div>
      </Modal>

      {/* Import reviews modal */}
      <Modal
        title="Импорт отзывов из Excel"
        open={importModalOpen}
        onCancel={() => {
          setImportModalOpen(false);
          setUploadFiles([]);
        }}
        onOk={async () => {
          if (!uploadFiles.length) {
            notify.error("Выберите Excel файл");
            return;
          }

          setUploading(true);
          try {
            const file = uploadFiles[0].originFileObj;
            if (!file) {
              notify.error("Файл не найден");
              return;
            }

            const formData = new FormData();
            formData.append('file', file);

            const apiUrl = resolveApiBase("http://localhost:8085");
            const response = await fetch(`${apiUrl}/api/v1/reviews/import`, {
              method: 'POST',
              body: formData,
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.detail || 'Ошибка импорта');
            }

            const result = await response.json();

            const message = `Импортировано: ${result.imported} из ${result.total} (пропущено: ${result.skipped})`;
            if (result.errors && result.errors.length > 0) {
              notify.warning('Импорт завершен с ошибками', message);
              console.error('Import errors:', result.errors);
            } else {
              notify.success('Импорт успешно завершен', message);
            }

            setImportModalOpen(false);
            setUploadFiles([]);
            tableQuery.refetch();
          } catch (error: any) {
            notify.error("Ошибка при импорте", error.message || String(error));
          } finally {
            setUploading(false);
          }
        }}
        confirmLoading={uploading}
        okText="Импортировать"
        cancelText="Отмена"
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Dragger
            accept=".xlsx,.xls,.csv"
            multiple={false}
            beforeUpload={() => false}
            fileList={uploadFiles}
            onChange={({ fileList }) => {
              const nextList = fileList.slice(-1);
              const nextFile = nextList[0];

              if (nextFile) {
                const fileName = (nextFile.name || "").toLowerCase();
                if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.csv')) {
                  notify.error("Выберите Excel или CSV файл");
                  return;
                }
              }

              setUploadFiles(nextList);
            }}
            onRemove={() => {
              setUploadFiles([]);
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Нажмите или перетащите файл в эту область</p>
            <p className="ant-upload-hint">
              Поддерживаются форматы: .xlsx, .xls, .csv
            </p>
          </Dragger>

          <Card size="small" title="Формат файла" style={{ background: '#f5f5f5' }}>
            <Space direction="vertical" size={8}>
              <Text strong style={{ fontSize: 13 }}>Wildberries (Excel):</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Колонки: ID отзыва, Дата, Артикул продавца, Артикул WB, Количество звезд, Бренд, Текст отзыва, Достоинства, Недостатки
              </Text>

              <Divider style={{ margin: '8px 0' }} />

              <Text strong style={{ fontSize: 13 }}>Ozon (Excel):</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Колонки: Артикул, Название товара, Текст отзыва, Дата публикации, Оценка, Количество фото
              </Text>
            </Space>
          </Card>
        </Space>
      </Modal>

      {/* Create review modal */}
      <Modal
        title="Добавить отзыв"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={async () => {
          try {
            const values = await createForm.validateFields();

            // Показываем уведомление о начале создания
            notify.info('Создание отзыва...', 'Пожалуйста, подождите');

            const response = await api.post('/reviews', {
              ...values,
              source: values.source || 'manual',
              status: 'new',
              created_at: new Date().toISOString(),
            });

            const newReview = response;

            setCreateModalOpen(false);
            createForm.resetFields();
            tableQuery.refetch();

            // Polling для отслеживания генерации ответа
            const pollForResponse = async () => {
              let attempts = 0;
              const maxAttempts = 40; // 40 * 3s = 2 минуты

              const checkStatus = async () => {
                try {
                  const response = await api.get<{ review: any }>(`/reviews/${newReview.id}`);
                  const reviewData = response.review;

                  // Если режим AI или шаблон - ждём генерацию ответа
                  if ((reviewData.routing_mode === 'ai' || reviewData.routing_mode === 'template')
                      && !reviewData.generated_response_text) {
                    // Продолжаем polling - генерация ещё не завершена
                    attempts++;
                    if (attempts < maxAttempts) {
                      setTimeout(checkStatus, 3000);
                    } else {
                      notify.info('Генерация ответа занимает больше времени', 'Проверьте статус позже');
                      tableQuery.refetch();
                    }
                    return;
                  }

                  // Показываем уведомление в зависимости от режима
                  if (reviewData.routing_mode === 'manual') {
                    notify.success('Отзыв обогащён', 'Требуется ручной ответ');
                  } else if (reviewData.generated_response_text) {
                    notify.success('Ответ сгенерирован');
                  } else {
                    notify.success('Отзыв создан и обработан');
                  }

                  tableQuery.refetch();
                } catch (error: any) {
                  console.error('Polling error:', error);
                }
              };

              // Start polling after 2 seconds
              setTimeout(checkStatus, 2000);
            };

            pollForResponse();
          } catch (error: any) {
            notify.error('Ошибка при создании отзыва', error.message || 'Неизвестная ошибка');
          }
        }}
        okText="Создать"
        cancelText="Отмена"
        width={700}
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="Источник" name="source" rules={[{ required: true, message: 'Выберите источник' }]}>
            <Select placeholder="Выберите источник">
              <Select.Option value="wildberries_cab1">Wildberries Кабинет 1</Select.Option>
              <Select.Option value="wildberries_cab2">Wildberries Кабинет 2</Select.Option>
              <Select.Option value="ozon">Ozon</Select.Option>
              <Select.Option value="manual">Ручной ввод</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="SKU товара" name="product_sku" rules={[{ required: true, message: 'Введите SKU' }]}>
            <Input placeholder="Артикул товара" />
          </Form.Item>

          <Form.Item label="Название товара" name="product_name" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder="Название товара" />
          </Form.Item>

          <Form.Item label="Бренд" name="brand" rules={[{ required: true, message: 'Выберите бренд' }]}>
            <Select placeholder="Выберите бренд" showSearch optionFilterProp="children">
              {availableBrands.map((brand) => (
                <Select.Option key={brand} value={brand}>
                  {brand}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Рейтинг" name="rating" rules={[{ required: true, message: 'Поставьте оценку' }]}>
            <Rate />
          </Form.Item>

          <Form.Item label="Имя автора" name="author_name">
            <Input placeholder="Имя автора отзыва" />
          </Form.Item>

          <Form.Item label="Достоинства" name="advantages">
            <TextArea rows={2} placeholder="Что понравилось" />
          </Form.Item>

          <Form.Item label="Недостатки" name="disadvantages">
            <TextArea rows={2} placeholder="Что не понравилось" />
          </Form.Item>

          <Form.Item label="Текст отзыва" name="text">
            <TextArea rows={4} placeholder="Основной текст отзыва (необязательно)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Export Modal */}
      <Modal
        title="Экспорт в Excel"
        open={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        onOk={async () => {
          try {
            const response = await fetch(`${restBaseUrl}/api/v1/reviews/export`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                review_ids: selectedRowKeys,
                fields: exportFields
              })
            });

            if (!response.ok) {
              throw new Error(`Ошибка ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `reviews_export_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            notify.success('Экспорт завершён');
            setExportModalOpen(false);
          } catch (error) {
            notify.error('Ошибка экспорта', String(error));
          }
        }}
        okText="Экспортировать"
        cancelText="Отмена"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Выбрано отзывов: {selectedRowKeys.length}
        </Text>

        <Checkbox.Group
          value={exportFields}
          onChange={(values) => setExportFields(values as string[])}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Checkbox value="date">Дата</Checkbox>
            <Checkbox value="product">Товар</Checkbox>
            <Checkbox value="sku">Артикул WB</Checkbox>
            <Checkbox value="brand">Бренд</Checkbox>
            <Checkbox value="rating">Рейтинг</Checkbox>
            <Checkbox value="text">Текст отзыва</Checkbox>
            <Checkbox value="advantages">Достоинства</Checkbox>
            <Checkbox value="disadvantages">Недостатки</Checkbox>
            <Checkbox value="author">Автор</Checkbox>
            <Checkbox value="generated_response">Сгенерированный ответ</Checkbox>
            <Checkbox value="final_response">Финальный ответ</Checkbox>
            <Checkbox value="status">Статус ответа</Checkbox>
            <Checkbox value="cabinet">Кабинет</Checkbox>
          </Space>
        </Checkbox.Group>
      </Modal>

      {/* Bulk Generate Modal */}
      <Modal
        title="Массовая генерация ответов"
        open={bulkGenerateModalOpen}
        onCancel={() => {
          if (!bulkGenerating) {
            setBulkGenerateModalOpen(false);
            setBulkGenerateMode('ai');
            setBulkGenerateTemplateId(undefined);
            setBulkGenerateStyleId(undefined);
          }
        }}
        onOk={async () => {
          setBulkGenerating(true);
          setBulkProgress({ done: 0, total: selectedRowKeys.length });

          let successCount = 0;
          let errorCount = 0;

          for (let i = 0; i < selectedRowKeys.length; i++) {
            const reviewId = selectedRowKeys[i];
            try {
              const payload: any = {};
              if (bulkGenerateMode === 'ai' && bulkGenerateStyleId) {
                payload.ai_style_id = bulkGenerateStyleId;
              }
              // Если выбран шаблон, генерируем через шаблон
              if (bulkGenerateMode === 'template' && bulkGenerateTemplateId) {
                payload.template_id = bulkGenerateTemplateId;
              }

              await api.post(`/reviews/${reviewId}/generate-response`, payload);
              successCount++;
            } catch (error) {
              errorCount++;
              console.error(`Error generating for ${reviewId}:`, error);
            }
            setBulkProgress({ done: i + 1, total: selectedRowKeys.length });
          }

          setBulkGenerating(false);
          setBulkGenerateModalOpen(false);
          setBulkGenerateMode('ai');
          setBulkGenerateTemplateId(undefined);
          setBulkGenerateStyleId(undefined);
          setSelectedRowKeys([]);
          tableQuery.refetch();

          if (errorCount === 0) {
            notify.success(`Сгенерировано ${successCount} ответов`);
          } else {
            notify.warning(`Сгенерировано: ${successCount}, ошибок: ${errorCount}`);
          }
        }}
        okText={bulkGenerating ? `Генерация... ${bulkProgress.done}/${bulkProgress.total}` : "Сгенерировать"}
        okButtonProps={{ disabled: bulkGenerating }}
        cancelButtonProps={{ disabled: bulkGenerating }}
        cancelText="Отмена"
        width={500}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Будет сгенерировано ответов: {selectedRowKeys.length}
        </Text>

        <Form layout="vertical">
          <Form.Item label="Режим генерации">
            <Radio.Group
              value={bulkGenerateMode}
              onChange={(e) => setBulkGenerateMode(e.target.value)}
            >
              <Radio value="ai">AI генерация</Radio>
              <Radio value="template">По шаблону</Radio>
            </Radio.Group>
          </Form.Item>

          {bulkGenerateMode === 'ai' && (
            <Form.Item label="Стиль AI">
              <Select
                placeholder="Использовать стиль по умолчанию"
                allowClear
                value={bulkGenerateStyleId}
                onChange={setBulkGenerateStyleId}
                options={aiStyles.map((s: any) => ({ value: s.id, label: s.name || s.style }))}
              />
            </Form.Item>
          )}

          {bulkGenerateMode === 'template' && (
            <Form.Item label="Шаблон" rules={[{ required: true }]}>
              <Select
                placeholder="Выберите шаблон"
                value={bulkGenerateTemplateId}
                onChange={setBulkGenerateTemplateId}
                options={templates.map((t: any) => ({
                  value: t.id,
                  label: `${t.name} [${t.category || 'Без категории'}]`
                }))}
              />
            </Form.Item>
          )}
        </Form>

        {bulkGenerating && (
          <div style={{ marginTop: 16 }}>
            <Text>Прогресс: {bulkProgress.done} / {bulkProgress.total}</Text>
            <div style={{
              height: 8,
              background: '#f0f0f0',
              borderRadius: 4,
              marginTop: 8,
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(bulkProgress.done / bulkProgress.total) * 100}%`,
                height: '100%',
                background: '#1890ff',
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        )}
      </Modal>
    </List>
  );
}
